import {
    Component,
    EventEmitter,
    Input,
    Output,
    SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { semesters, years } from '../../assets/date';

/**
 * Componente de selección de semestre
 *
 * Este SemesterPicker permite seleccionar:
 * - Un año
 * - Un semestre
 *
 * Emite un valor final en formato `YYYY-S` dependiendo el semestre requerido (1-2).
 *
 * Se usa principalmente para filtros de fechas en reportes.
 *
 * @component
 * @selector app-semester-picker
 * @standalone true
 * @imports CommonModule, FormsModule
 */
@Component({
    selector: 'app-semester-picker',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './semester-picker.component.html',
    styleUrls: ['./semester-picker.component.css'],
})
export class SemesterPickerComponent {
    @Input() placeholder = 'Seleccionar fecha';
    @Input() dateValue = ''; // Formato YYYY-S
    @Output() dateSelected = new EventEmitter<string>();
    @Input() hasError: boolean = false;

    // Variables para el selector de fecha
    showDatePicker = false;
    selectingYear = true;
    selectedYear: number = new Date().getFullYear();
    selectedSemester: number = 1;

    private pendingValidation: boolean = false;
    years = years;
    semesters = semesters;

    ngOnInit() {
        if (this.dateValue) {
            // Parsear el string YYYY-S
            const [year, semester] = this.dateValue.split('-').map(Number);
            this.selectedYear = year;
            this.selectedSemester = semester;
        }
    }

    /**
     * Detecta cambios en las propiedades @Input
     */
    ngOnChanges() {
        if (this.pendingValidation) {
            if (!this.hasError) {
                this.closeDatePicker();
            }
            this.pendingValidation = false;
        }
    }

    /**
     * Devuelve la fecha formateada en texto (ejemplo: "Enero a Junio 2025").
     */
    get formattedDate(): string {
        if (!this.dateValue) return '';
        const [year, semester] = this.dateValue.split('-').map(Number);
        const semesterObj = this.semesters.find((s) => s.value === semester);
        return `${semesterObj?.name} ${year}`;
    }

    /**
     * Abre el selector de fecha y establece valores iniciales.
     */
    openDatePicker() {
        this.selectingYear = true;
        this.showDatePicker = true;
        this.pendingValidation = false;

        // Si ya hay una fecha seleccionada, usarla como valores iniciales
        if (this.dateValue) {
            const [year, semester] = this.dateValue.split('-').map(Number);
            this.selectedYear = year;
            this.selectedSemester = semester;
        } else {
            // Valores por defecto
            this.selectedYear = new Date().getFullYear();
            this.selectedSemester = 1;
        }
    }

    /**
     * Cierra el selector de fecha.
     */
    closeDatePicker() {
        this.showDatePicker = false;
        this.selectingYear = true;
        this.pendingValidation = false;
    }

    /**
     * Finaliza la selección de fecha, emitiendo un valor en formato YYYY-S.
     */
    selectDate() {
        // Formatear como YYYY-S
        const formattedDate = `${this.selectedYear}-${this.selectedSemester}`;

        this.dateSelected.emit(formattedDate);
        this.pendingValidation = true;

        // Si el padre ya tiene el mismo valor y es válido, se necesita un timeout
        // para dar tiempo a que el padre procese y actualice hasError
        setTimeout(() => {
            // Si después del timeout sigue pendiente y no hay error, se fuerza el cierre
            if (this.pendingValidation && !this.hasError) {
                this.closeDatePicker();
            }
        }, 200);
    }
}
